import { apiClient } from './api'

/**
 * AI Concierge client (attendee-facing).
 *
 * `getEventConfig` gates the whole UI: the widget only renders when the event
 * reports enabled=true (global AND per-event gates satisfied on the backend).
 *
 * `ask` streams the answer. The backend returns Server-Sent Events over a POST
 * body, which EventSource can't do (it's GET-only), so we read the response
 * body as a stream with fetch. Events: `meta` {conversationId}, `token` {text},
 * `done` {conversationId}, `error` {message}.
 */

export interface ConciergeMessage {
  role: 'user' | 'assistant'
  content: string
  createdAt?: string | null
}

export interface AskHandlers {
  onMeta?: (conversationId: string) => void
  onToken: (text: string) => void
  onDone?: (conversationId: string | null) => void
  onError?: (message: string) => void
}

/** Resolve the API base the same way apiClient does (runtime config or /api). */
function apiBase(): string {
  const base = apiClient.defaults.baseURL ?? '/api'
  return base.replace(/\/$/, '')
}

export const conciergeService = {
  /** Global availability (mirrors pushService.getConfig). */
  getConfig: () =>
    apiClient
      .get<{ enabled: boolean }>('/concierge/config')
      .then((r) => r.data)
      .catch(() => ({ enabled: false })),

  /** Effective availability for a specific event (global AND per-event). */
  getEventConfig: (eventId: string) =>
    apiClient
      .get<{ enabled: boolean }>(`/events/${eventId}/concierge/config`)
      .then((r) => r.data)
      .catch(() => ({ enabled: false })),

  /** The signed-in attendee's prior turns (to resume a conversation). */
  getHistory: (eventId: string, conversationId?: string) =>
    apiClient
      .get<{ conversationId: string | null; messages: ConciergeMessage[] }>(
        `/events/${eventId}/concierge/history`,
        { params: conversationId ? { conversationId } : undefined },
      )
      .then((r) => r.data)
      .catch(() => ({ conversationId: null, messages: [] })),

  /**
   * Ask a question and stream the answer. Returns an AbortController so the
   * caller can cancel an in-flight stream (e.g. on unmount). Resolves when the
   * stream completes.
   */
  ask(
    eventId: string,
    question: string,
    conversationId: string | null,
    handlers: AskHandlers,
  ): { controller: AbortController; done: Promise<void> } {
    const controller = new AbortController()
    const done = (async () => {
      let resp: Response
      try {
        resp = await fetch(`${apiBase()}/events/${eventId}/concierge/ask`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ question, conversationId }),
          signal: controller.signal,
        })
      } catch {
        handlers.onError?.('Could not reach the assistant. Please try again.')
        return
      }

      if (!resp.ok || !resp.body) {
        let msg = 'The assistant is unavailable right now.'
        try {
          const data = await resp.json()
          if (data?.detail) msg = data.detail
        } catch {
          /* keep default */
        }
        handlers.onError?.(msg)
        return
      }

      const reader = resp.body.getReader()
      const decoder = new TextDecoder()
      let buffer = ''
      try {
        for (;;) {
          const { value, done: streamDone } = await reader.read()
          if (streamDone) break
          buffer += decoder.decode(value, { stream: true })
          // SSE frames are separated by a blank line.
          let sep: number
          while ((sep = buffer.indexOf('\n\n')) !== -1) {
            const frame = buffer.slice(0, sep)
            buffer = buffer.slice(sep + 2)
            dispatchFrame(frame, handlers)
          }
        }
      } catch {
        if (!controller.signal.aborted) {
          handlers.onError?.('The connection was interrupted.')
        }
      }
    })()
    return { controller, done }
  },
}

function dispatchFrame(frame: string, handlers: AskHandlers): void {
  let event = 'message'
  const dataLines: string[] = []
  for (const line of frame.split('\n')) {
    if (line.startsWith('event:')) event = line.slice(6).trim()
    else if (line.startsWith('data:')) dataLines.push(line.slice(5).trim())
  }
  if (dataLines.length === 0) return
  let data: Record<string, unknown> = {}
  try {
    data = JSON.parse(dataLines.join('\n'))
  } catch {
    return
  }
  switch (event) {
    case 'meta':
      handlers.onMeta?.(String(data.conversationId ?? ''))
      break
    case 'token':
      if (typeof data.text === 'string') handlers.onToken(data.text)
      break
    case 'done':
      handlers.onDone?.((data.conversationId as string) ?? null)
      break
    case 'error':
      handlers.onError?.(String(data.message ?? 'Something went wrong.'))
      break
  }
}
